import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Empty, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";

const ACCOUNTS = {
  "5200": "Programme & event costs", "5300": "Running costs", "5301": "Utilities",
  "5302": "Housekeeping", "5303": "Office supplies", "5304": "Repairs",
  "5305": "Statutory costs", "5306": "Technology",
};
const accountName = (code) => ACCOUNTS[code] || "Other running costs";

const BLANK = { code: "", name: "", category: "utilities", account_code: "5300",
  account_name: "Running costs", owner: "", notes: "", active: true };

const Field = ({ label, children, className }) => (
  <div className={className}>
    <Label className="mb-1 block text-[10.5px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);

// Everyday running costs that belong to no programme and no event: electricity, cleaning,
// stationery, the annual audit fee. The Treasurer keeps this list.
export const CostCentres = () => {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data: d } = await api.get("/charity/cost-centres");
    setData(d);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      if (editing) {
        await api.put(`/charity/cost-centres/${editing}`, form);
        toast.success(`${form.code} updated`);
      } else {
        await api.post("/charity/cost-centres", form);
        toast.success(`${form.code} added — it can now be chosen on an expense`);
      }
      setForm(BLANK);
      setEditing("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const remove = async (code) => {
    setBusy(code);
    try {
      await api.delete(`/charity/cost-centres/${code}`);
      toast.success(`${code} removed`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return null;
  const writable = can("finance", "write");

  return (
    <Panel testId="panel-cost-centres" kicker="FR-FIN-016" title="Cost centres for everyday running costs"
           subtitle="Electricity, cleaning materials, stationery, maintenance, statutory fees — spending that belongs to no programme and no event. Whatever is listed here can be chosen when an expense or a claim is raised.">
      <Table testId="cost-centres-table"
             head={<><Th>Code</Th><Th>Cost centre</Th><Th>Category</Th><Th>Ledger account</Th>
               <Th>Spent to date</Th><Th>State</Th><Th /></>}>
        {data.items.length === 0 && <Empty colSpan={7} label="No cost centres yet" />}
        {data.items.map((c) => (
          <tr key={c.code} className="row-hover" data-testid={`cost-centre-row-${c.code}`}>
            <Td className="font-mono text-[11px]">{c.code}</Td>
            <Td className="text-[12px]">
              {c.name}
              {c.notes && <div className="text-[10.5px] text-muted-foreground">{c.notes}</div>}
            </Td>
            <Td className="text-[11.5px] capitalize text-muted-foreground">{c.category}</Td>
            <Td className="font-mono text-[10.5px] text-muted-foreground">
              {c.account_code} · {c.account_name}
            </Td>
            <Td className="font-mono text-[11.5px]" data-testid={`cost-centre-spent-${c.code}`}>
              {money(c.spent || 0, "INR", true)}
            </Td>
            <Td>
              <Chip className={c.active === false
                ? "border-slate-500/30 bg-slate-500/10 text-slate-600 dark:text-slate-300"
                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"}>
                {c.active === false ? "inactive" : "in use"}
              </Chip>
            </Td>
            <Td className="w-[80px]">
              {writable && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-6 px-1.5"
                          data-testid={`cost-centre-edit-${c.code}`}
                          onClick={() => { setForm({ ...BLANK, ...c }); setEditing(c.code); }}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5"
                          data-testid={`cost-centre-delete-${c.code}`}
                          disabled={busy === c.code} onClick={() => remove(c.code)}>
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              )}
            </Td>
          </tr>
        ))}
      </Table>

      {writable && (
        <div className="grid grid-cols-2 items-end gap-2 border-t border-border p-3 sm:grid-cols-6">
          <Field label="Code">
            <Input value={form.code} disabled={Boolean(editing)} data-testid="cost-centre-code-input"
                   onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                   className="h-8 bg-muted/40 font-mono text-[12px]" />
          </Field>
          <Field label="Name" className="sm:col-span-2">
            <Input value={form.name} data-testid="cost-centre-name-input"
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="h-8 bg-muted/40 text-[12px]" />
          </Field>
          <Field label="Category">
            <Select value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="cost-centre-category-select"
                             className="h-8 bg-muted/40 text-[12px] capitalize">
                <SelectValue /></SelectTrigger>
              <SelectContent>
                {data.categories.map((c) => (
                  <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Ledger account">
            <div className="flex items-center gap-2">
              <Input value={form.account_code} data-testid="cost-centre-account-input"
                     onChange={(e) => setForm({ ...form, account_code: e.target.value,
                                                account_name: accountName(e.target.value) })}
                     className="h-8 w-20 bg-muted/40 font-mono text-[12px]" />
              <span className="truncate text-[11px] text-muted-foreground"
                    data-testid="cost-centre-account-name">
                {accountName(form.account_code)}
              </span>
            </div>
          </Field>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={!form.code || !form.name || busy === "save"}
                    data-testid="cost-centre-save-button"
                    className="h-8 rounded-full bg-primary text-[11px] text-primary-foreground">
              {busy === "save" ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                : <Plus className="mr-1.5 h-3 w-3" />} {editing ? "Update" : "Add"}
            </Button>
            {editing && (
              <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]"
                      data-testid="cost-centre-cancel-button"
                      onClick={() => { setForm(BLANK); setEditing(""); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
};

export default CostCentres;
